import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Check, X, Clock, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

export default function DeletionRequestsPage() {
  const { isSuperAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/deletion-requests${params}`);
      setRequests(res.data);
    } catch (err) {
      console.error("Failed to fetch deletion requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId, action) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/deletion-requests/${requestId}/${action}`);
      toast.success(`Request ${action === "approve" ? "approved" : "rejected"}`);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deletion-requests-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
            {isSuperAdmin ? "Deletion Requests" : "My Deletion Requests"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isSuperAdmin
              ? "Review and approve deletion requests from admins"
              : "Track your deletion requests"}
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] rounded-sm" data-testid="filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Type</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Name</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Requested By</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Reason</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Date</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Status</TableHead>
                {isSuperAdmin && (
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.request_id} data-testid={`del-req-${req.request_id}`}>
                  <TableCell>
                    <Badge variant="outline" className="capitalize font-mono text-xs">
                      {req.entity_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{req.entity_name}</TableCell>
                  <TableCell className="text-muted-foreground">{req.requested_by_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                    {req.reason || "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                    {formatDate(req.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_STYLES[req.status]} capitalize text-xs`}>
                      {req.status}
                    </Badge>
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell className="text-right">
                      {req.status === "pending" ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleAction(req.request_id, "approve")}
                            disabled={processing === req.request_id}
                            data-testid={`approve-${req.request_id}`}
                          >
                            {processing === req.request_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-destructive hover:text-destructive hover:bg-red-50"
                            onClick={() => handleAction(req.request_id, "reject")}
                            disabled={processing === req.request_id}
                            data-testid={`reject-${req.request_id}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" />
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {req.reviewed_by_name && `by ${req.reviewed_by_name}`}
                        </span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {requests.length === 0 && (
            <div className="text-center py-12">
              <Trash2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No {filter !== "all" ? filter : ""} deletion requests</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
